import api, {
  withTenantContextSnapshot,
  type TenantContextSnapshot,
} from "@/lib/api";
import type { ApiEnvelope, PaginatedResponse } from "@/lib/types";
import type { ProjectZone } from "../types";

const PAGE_SIZE = 200;
const MAX_PAGES = 1000;
const ORDERING = "name,id";
const INCOMPLETE =
  "No se ha podido cargar la lista completa de zonas. Vuelve a intentarlo.";

function checkCancelled(signal?: AbortSignal) {
  if (signal?.aborted)
    throw new DOMException("La carga de zonas se ha cancelado.", "AbortError");
}

/** Inspect a receipt, never send credentials to its URL. All requests stay local. */
function nextPage(next: string, current: number, projectId: string): number {
  const base =
    api.defaults?.baseURL ?? import.meta.env.VITE_API_URL ?? "/api/v1";
  const endpoint = new URL(
    `${base.replace(/\/$/, "")}/geo/zones/`,
    window.location.origin,
  );
  const url = new URL(next, endpoint);
  if (
    !["http:", "https:"].includes(url.protocol) ||
    url.pathname !== endpoint.pathname ||
    url.username ||
    url.password ||
    url.hash
  )
    throw new Error(INCOMPLETE);
  const permitted = new Set(["page", "project", "page_size", "ordering"]);
  for (const key of url.searchParams.keys()) {
    if (!permitted.has(key) || url.searchParams.getAll(key).length !== 1)
      throw new Error(INCOMPLETE);
  }
  const raw = url.searchParams.get("page") ?? "";
  const page = Number(raw);
  if (
    !/^[1-9]\d*$/.test(raw) ||
    !Number.isSafeInteger(page) ||
    page !== current + 1 ||
    page > MAX_PAGES
  )
    throw new Error(INCOMPLETE);
  for (const [key, value] of [
    ["project", projectId],
    ["page_size", String(PAGE_SIZE)],
    ["ordering", ORDERING],
  ]) {
    if (url.searchParams.has(key!) && url.searchParams.get(key!) !== value)
      throw new Error(INCOMPLETE);
  }
  return page;
}

export async function fetchAllProjectZones(
  projectId: string,
  context: TenantContextSnapshot,
  signal?: AbortSignal,
): Promise<ApiEnvelope<ProjectZone[]>> {
  const tenant = Object.freeze({
    organization: context.organization,
    workspace: context.workspace,
  });
  const zones = new Map<string, ProjectZone>();
  let first:
    | ApiEnvelope<ProjectZone[] | PaginatedResponse<ProjectZone>>
    | undefined;
  let total: number | undefined;
  let page = 1;
  while (page <= MAX_PAGES) {
    checkCancelled(signal);
    const { data: receipt } = await api.get<
      ApiEnvelope<ProjectZone[] | PaginatedResponse<ProjectZone>>
    >(
      "/geo/zones/",
      withTenantContextSnapshot(tenant, {
        params: {
          project: projectId,
          page,
          page_size: PAGE_SIZE,
          ordering: ORDERING,
        },
        signal,
        timeout: 30_000,
      }),
    );
    checkCancelled(signal);
    if (!receipt || typeof receipt !== "object" || receipt.status === "error")
      throw new Error(INCOMPLETE);
    first ??= receipt;
    const raw = receipt.data;
    const rows = Array.isArray(raw) ? raw : raw?.results;
    const count = Array.isArray(raw) ? receipt.meta?.total : raw?.count;
    const next = Array.isArray(raw) ? receipt.meta?.next : raw?.next;
    if (!Array.isArray(rows) || rows.length > MAX_PAGES * PAGE_SIZE)
      throw new Error(INCOMPLETE);
    const paginated = count !== undefined || next !== undefined;
    if (paginated && rows.length > PAGE_SIZE) throw new Error(INCOMPLETE);
    if (
      paginated &&
      (!Number.isSafeInteger(count) ||
        count! < 0 ||
        count! > MAX_PAGES * PAGE_SIZE ||
        (next !== null && typeof next !== "string"))
    )
      throw new Error(INCOMPLETE);
    if (receipt.meta?.page !== undefined && receipt.meta.page !== page)
      throw new Error(INCOMPLETE);
    if (total !== undefined && count !== total)
      throw new Error(
        "La lista de zonas ha cambiado durante la carga. Vuelve a intentarlo.",
      );
    total ??= count;
    const before = zones.size;
    for (const zone of rows) {
      if (
        typeof zone.id !== "string" ||
        !zone.id ||
        ("project" in zone && zone.project !== projectId)
      )
        throw new Error(INCOMPLETE);
      zones.set(zone.id, zone);
    }
    if (!paginated || next === null) {
      if (total !== undefined && zones.size !== total)
        throw new Error(INCOMPLETE);
      const meta = {
        ...first.meta,
        total: zones.size,
        next: null,
        previous: null,
      };
      delete meta.page;
      delete meta.page_size;
      return { ...first, data: Array.from(zones.values()), meta };
    }
    if (zones.size === before || zones.size >= count!)
      throw new Error(INCOMPLETE);
    page = nextPage(next!, page, projectId);
  }
  throw new Error(INCOMPLETE);
}
